import type { Media } from '@/payload-types'

const MEDIA_API_PREFIX = '/api/media/file/'
const LOGO_DISPLAY_HEIGHT = 36
const LOGO_MAX_WIDTH = 144

type MediaSize = keyof NonNullable<Media['sizes']>

function withCacheBust(url: string, version?: string | null): string {
  if (!version) return url
  const param = `v=${encodeURIComponent(version)}`
  return `${url}${url.includes('?') ? '&' : '?'}${param}`
}

/** Resolve Payload proxy paths to direct Vercel Blob CDN URLs. */
export function resolveMediaUrl(url: string | null | undefined): string {
  if (!url) return ''
  if (url.startsWith('http://') || url.startsWith('https://')) return url
  if (url.startsWith(MEDIA_API_PREFIX)) {
    const base = process.env.NEXT_PUBLIC_BLOB_BASE_URL
    if (base) {
      const filename = url.slice(MEDIA_API_PREFIX.length)
      return `${base}/${filename}`
    }
  }
  return url
}

/** Pick the best URL from a Media doc, optionally preferring a named size variant. */
export function getMediaUrl(
  media: Media | string | null | undefined,
  size?: MediaSize,
  options?: { cacheBust?: boolean },
): string {
  if (!media) return ''
  if (typeof media === 'string') return resolveMediaUrl(media)

  const sized = size ? media.sizes?.[size] : undefined
  const url = resolveMediaUrl(sized?.url || media.url)
  if (!url) return ''

  return options?.cacheBust ? withCacheBust(url, media.updatedAt) : url
}

/** Shorthand for hardcoded Payload media filenames (without the /api/media/file/ prefix). */
export function mediaFile(filename: string): string {
  return resolveMediaUrl(`${MEDIA_API_PREFIX}${filename}`)
}

/** Absolute URL for Open Graph / metadata (blob URLs pass through as-is). */
export function getAbsoluteMediaUrl(
  media: Media | string | null | undefined,
  size?: MediaSize,
): string {
  const url = getMediaUrl(media, size)
  if (!url) return ''
  if (url.startsWith('http://') || url.startsWith('https://')) return url

  const base = process.env.NEXT_PUBLIC_SERVER_URL || 'https://serottelaw.com'
  return `${base}${url.startsWith('/') ? url : `/${url}`}`
}

export function getLogoImage(logo: Media): { src: string; width: number; height: number } | null {
  const sized = logo.sizes?.logo
  const url = resolveMediaUrl(sized?.url || logo.url)
  if (!url) return null

  const intrinsicWidth = sized?.width ?? logo.width ?? LOGO_MAX_WIDTH
  const intrinsicHeight = sized?.height ?? logo.height ?? LOGO_DISPLAY_HEIGHT

  const aspectWidth = Math.round((intrinsicWidth / intrinsicHeight) * LOGO_DISPLAY_HEIGHT)
  const displayWidth = Math.min(Math.max(aspectWidth, 32), LOGO_MAX_WIDTH)

  return {
    src: withCacheBust(url, logo.updatedAt),
    width: displayWidth,
    height: LOGO_DISPLAY_HEIGHT,
  }
}
